<template>
  <div class="forging-room">
    <div class="room-header">
      <div class="header-info">
        <div class="room-icon" style="background: linear-gradient(135deg, #2db7f5 0%, #667eea 100%)">
          <Icon type="ios-hammer" size="24" color="#fff" />
        </div>
        <div>
          <h3 class="room-title">炼器室</h3>
          <p class="room-desc">锻造装备增强战力</p>
        </div>
      </div>
      <div class="header-stats">
        <div class="stat-item">
          <span class="stat-label">已分配</span>
          <span class="stat-value">{{ points }}时辰</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">成功率加成</span>
          <span class="stat-value">+{{ successBonus }}%</span>
        </div>
      </div>
      <Button type="info" size="small" @click="$emit('upgrade')">
        <Icon type="ios-arrow-up" /> Lv.{{ level }}
      </Button>
    </div>

    <div class="room-content">
      <!-- 主标签页 -->
      <div class="main-tabs">
        <div
          v-for="tab in mainTabs"
          :key="tab.key"
          :class="['main-tab', { active: activeMainTab === tab.key }]"
          @click="switchMainTab(tab.key)"
        >
          <Icon :type="tab.icon" />
          <span>{{ tab.label }}</span>
          <span class="tab-count" v-if="tab.count > 0">{{ tab.count }}</span>
        </div>
      </div>

      <!-- 图纸锻造 -->
      <div v-if="activeMainTab === 'blueprints'" class="tab-content">
        <div class="forge-layout">
          <div class="blueprint-list">
            <div class="list-header">
              <Input
                v-model="blueprintSearch"
                placeholder="搜索图纸..."
                size="small"
                clearable
              >
                <Icon type="ios-search" slot="prefix" />
              </Input>
              <div class="filter-tags">
                <span
                  v-for="filter in blueprintFilters"
                  :key="filter.value"
                  :class="['filter-tag', { active: blueprintFilter === filter.value }]"
                  @click="blueprintFilter = filter.value"
                >
                  {{ filter.label }}
                </span>
              </div>
            </div>

            <div class="blueprint-items">
              <div
                v-for="(item, index) in filteredBlueprints"
                :key="item.formulaId || index"
                :class="['blueprint-item', {
                  active: selectedBlueprint && selectedBlueprint.formulaId === item.formulaId,
                  'can-forge': item.isMeet
                }]"
                @click="selectBlueprint(item, index)"
              >
                <div class="blueprint-header">
                  <div class="blueprint-name">{{ item.itemName }}</div>
                  <span v-if="item.isMeet" class="status-tag success">可锻造</span>
                </div>
                <div class="blueprint-info">
                  <span>{{ item.typeDec || '装备' }}</span>
                </div>
              </div>

              <div class="empty-state" v-if="filteredBlueprints.length === 0">
                <Icon type="ios-hammer-outline" size="32" />
                <p>暂无图纸</p>
              </div>
            </div>
          </div>

          <div class="blueprint-detail">
            <template v-if="selectedBlueprint">
              <div class="detail-header">
                <h3>{{ selectedBlueprint.itemName }}</h3>
                <div class="detail-desc" v-if="selectedBlueprint.desci || selectedBlueprint.decs">
                  {{ selectedBlueprint.desci || selectedBlueprint.decs }}
                </div>
              </div>

              <div class="materials-card">
                <div class="card-header">
                  <span>所需材料</span>
                  <span :class="['material-status', { ready: selectedBlueprint.isMeet }]">
                    {{ selectedBlueprint.isMeet ? '材料齐全' : '材料不足' }}
                  </span>
                </div>
                <div class="materials-list">
                  <div
                    v-for="(mat, index) in selectedBlueprint.matList"
                    :key="index"
                    :class="['material-item', {
                      sufficient: isMaterialFullyProvided(mat),
                      optional: isOptionalMaterial(mat)
                    }]"
                  >
                    <div class="material-info">
                      <span class="material-type">{{ isOptionalMaterial(mat) ? '辅材（可选）' : '主材' }}</span>
                      <span class="material-name">{{ mat.itemName }}</span>
                    </div>
                    <div class="material-quantity">
                      <span>{{ mat.haveNum }}/{{ mat.num }}</span>
                      <Icon
                        :type="getMaterialStatusIcon(mat)"
                        :color="getMaterialStatusColor(mat)"
                        size="16"
                      />
                    </div>
                    <div class="material-input" v-if="isOptionalMaterial(mat)">
                      <InputNumber
                        v-model="itemFucaiNum[index]"
                        :min="0"
                        :max="mat.haveNum"
                        :step="1"
                        size="small"
                        @on-change="(val) => handleMaterialChange(mat.itemId, index, val)"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="forge-action">
                <div class="action-info">
                  <span>成功率: {{ ((selectedBlueprint.basicRate || 0) + (selectedBlueprint.caiRate || 0) + successBonus / 100) * 100 }}%</span>
                  <span>耗时: {{ selectedBlueprint.costTime }}秒</span>
                  <span>消耗: {{ selectedBlueprint.costCoin }}灵石</span>
                </div>
                <Button
                  type="primary"
                  size="large"
                  long
                  :disabled="!selectedBlueprint.isMeet"
                  :loading="forging"
                  @click="startForging"
                >
                  <Icon type="ios-hammer" />
                  立即锻造
                </Button>
                <div v-if="!selectedBlueprint.isMeet" class="action-hint warning">
                  材料不足，无法锻造
                </div>
              </div>
            </template>

            <div class="no-selection" v-else>
              <Icon type="ios-hammer-outline" size="64" />
              <h3>选择图纸开始锻造</h3>
              <p>从左侧选择一张图纸</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 法宝重铸 -->
      <div v-if="activeMainTab === 'equipment'" class="tab-content">
        <div class="sub-tabs">
          <div
            v-for="sub in equipmentSubTabs"
            :key="sub.key"
            :class="['sub-tab', { active: activeSubTab === sub.key }]"
            @click="switchSubTab(sub.key)"
          >
            {{ sub.label }}
          </div>
        </div>

        <div class="equipment-layout">
          <div class="equipment-list">
            <div class="list-header">
              <Input
                v-model="equipmentSearch"
                placeholder="搜索装备..."
                size="small"
                clearable
              >
                <Icon type="ios-search" slot="prefix" />
              </Input>
              <Button size="small" :loading="equipmentLoading" @click="loadEquipmentList">
                刷新
              </Button>
            </div>

            <div class="equipment-items">
              <div
                v-for="(item, index) in filteredEquipments"
                :key="item.packItemId || index"
                :class="['equipment-item', { active: selectedEquip && selectedEquip.packItemId === item.packItemId }]"
                @click="selectEquipment(item, index)"
              >
                <div class="equip-name-row">
                  <div class="equip-name">{{ item.itemName }}</div>
                  <div class="equip-badge">
                    <span v-if="item.enhanLevel" class="equip-enhance">+{{ item.enhanLevel }}</span>
                    <span v-if="item.enhanRange" class="equip-range">{{ (item.enhanRange * 100).toFixed(0) }}%</span>
                  </div>
                </div>
                <div class="equip-info">
                  <span>{{ item.typeDec || '法宝' }}</span>
                  <span>Lv.{{ item.level || 1 }}</span>
                </div>
                <div class="equip-base-stats" v-if="hasBaseStats(item)">
                  <span v-if="item.life" class="stat-tag">生命{{ item.life }}</span>
                  <span v-if="item.mana" class="stat-tag">真气{{ item.mana }}</span>
                  <span v-if="item.attack" class="stat-tag">攻击{{ item.attack }}</span>
                  <span v-if="item.magAttack" class="stat-tag">灵攻{{ item.magAttack }}</span>
                  <span v-if="item.defense" class="stat-tag">防御{{ item.defense }}</span>
                  <span v-if="item.magicDefense" class="stat-tag">灵防{{ item.magicDefense }}</span>
                </div>
                <div class="equip-shen-stats" v-if="item.physique || item.dexterous || item.spirit">
                  <span v-if="item.physique" class="shen-tag">根骨+{{ item.physique }}</span>
                  <span v-if="item.dexterous" class="shen-tag">力道+{{ item.dexterous }}</span>
                  <span v-if="item.spirit" class="shen-tag">灵力+{{ item.spirit }}</span>
                </div>
              </div>

              <div class="empty-state" v-if="filteredEquipments.length === 0">
                <Icon type="ios-cube-outline" size="32" />
                <p>暂无法宝</p>
              </div>
            </div>
          </div>

          <div class="equipment-detail">
            <template v-if="selectedEquip">
              <div class="detail-header">
                <h3>{{ selectedEquip.itemName }}</h3>
              </div>

              <!-- 法宝熔炼 -->
              <div v-if="activeSubTab === 'smelt'" class="action-panel">
                <div class="panel-title">法宝熔炼</div>
                <div class="smelt-materials">
                  <div class="material-search">
                    <Input
                      v-model="materialSearch"
                      placeholder="搜索熔炼材料..."
                      size="small"
                      clearable
                    >
                      <Icon type="ios-search" slot="prefix" />
                    </Input>
                    <Button size="small" :loading="materialLoading" @click="loadSmeltMaterials">
                      刷新
                    </Button>
                  </div>
                  <div class="material-list">
                    <div
                      v-for="mat in filteredSmeltMaterials"
                      :key="mat.packItemId"
                      :class="['smelt-material', { active: selectedSmeltMaterialId === mat.packItemId }]"
                      @click="selectedSmeltMaterialId = mat.packItemId"
                    >
                      <span class="mat-name">{{ mat.itemName }}</span>
                      <span class="mat-count">x{{ mat.itemNum }}</span>
                    </div>
                  </div>
                </div>
                <Button
                  type="primary"
                  size="large"
                  long
                  :disabled="!selectedSmeltMaterialId"
                  :loading="forgeSubmitting"
                  @click="submitSmelt"
                  style="margin-top: 16px"
                >
                  开始熔炼
                </Button>
              </div>

              <!-- 秘宝打造 -->
              <div v-if="activeSubTab === 'treasure'" class="action-panel">
                <div class="panel-title">秘宝打造</div>
                <div class="treasure-info" v-if="forgeOptions.treasureForge">
                  <div class="info-row">
                    <span>目标秘宝:</span>
                    <span>{{ forgeOptions.treasureForge.targetItemName || '--' }}</span>
                  </div>
                  <div class="info-row">
                    <span>秘宝方向:</span>
                    <Select v-model="treasureBattleType" size="small" style="width: 120px">
                      <Option
                        v-for="opt in (forgeOptions.treasureForge.battleOptions || ['PVE', 'PVP'])"
                        :key="opt"
                        :value="opt"
                      >
                        {{ opt }}
                      </Option>
                    </Select>
                  </div>
                  <div class="info-row">
                    <span>打造规则:</span>
                    <span>{{ getTreasureForgeRuleText() }}</span>
                  </div>
                  <div class="treasure-warning" v-if="needsTreasureForgeLimitWarning()">
                    <Icon type="ios-warning" />
                    <span>{{ getTreasureForgeLimitWarning() }}</span>
                  </div>
                  <div class="info-row">
                    <span>陨铁消耗:</span>
                    <span>{{ forgeOptions.treasureForge.meteoriteNeed || 0 }}</span>
                  </div>
                  <div class="info-row">
                    <span>当前拥有:</span>
                    <span :class="{ insufficient: forgeOptions.treasureForge.meteoriteHave < forgeOptions.treasureForge.meteoriteNeed }">
                      {{ forgeOptions.treasureForge.meteoriteHave || 0 }}
                    </span>
                  </div>
                  <div class="info-row" v-if="Number(forgeOptions.treasureForge.extraMaterialNeed || 0) > 0">
                    <span>{{ forgeOptions.treasureForge.extraMaterialItemName || '额外材料' }}:</span>
                    <span :class="{ insufficient: forgeOptions.treasureForge.extraMaterialHave < forgeOptions.treasureForge.extraMaterialNeed }">
                      {{ forgeOptions.treasureForge.extraMaterialHave || 0 }}/{{ forgeOptions.treasureForge.extraMaterialNeed || 0 }}
                    </span>
                  </div>
                </div>
                <Button
                  type="primary"
                  size="large"
                  long
                  :disabled="!(forgeOptions.treasureForge && forgeOptions.treasureForge.canDo)"
                  :loading="forgeSubmitting"
                  @click="submitTreasureForge"
                  style="margin-top: 16px"
                >
                  发起秘宝打造
                </Button>
              </div>

              <!-- 五行轮转 -->
              <div v-if="activeSubTab === 'element'" class="action-panel">
                <div class="panel-title">五行轮转</div>
                <div class="element-info" v-if="forgeOptions.elementRotate">
                  <div class="info-row">
                    <span>当前属性:</span>
                    <span>{{ getElementName(selectedEquip.elment) }}</span>
                  </div>
                  <div class="info-row">
                    <span>结晶消耗:</span>
                    <span>{{ forgeOptions.elementRotate.crystalNeed || 0 }}</span>
                  </div>
                  <div class="info-row">
                    <span>当前拥有:</span>
                    <span :class="{ insufficient: forgeOptions.elementRotate.crystalHave < forgeOptions.elementRotate.crystalNeed }">
                      {{ forgeOptions.elementRotate.crystalHave || 0 }}
                    </span>
                  </div>
                </div>
                <Button
                  type="primary"
                  size="large"
                  long
                  :disabled="!(forgeOptions.elementRotate && forgeOptions.elementRotate.canDo)"
                  :loading="forgeSubmitting"
                  @click="submitElementRotate"
                  style="margin-top: 16px"
                >
                  发起五行轮转
                </Button>
              </div>

              <!-- 淬炼升华 -->
              <div v-if="activeSubTab === 'sublimation'" class="action-panel">
                <div class="panel-title">淬炼升华</div>
                <div class="sublimation-info" v-if="forgeOptions.sublimation">
                  <div class="info-row">
                    <span>当前幅度:</span>
                    <span>{{ forgeOptions.sublimation.currentRangePercent || 0 }}%</span>
                  </div>
                  <div class="info-row">
                    <span>剩余可补:</span>
                    <span>{{ forgeOptions.sublimation.remainingPercent || 0 }}%</span>
                  </div>
                  <div class="info-row">
                    <span>一键补满金币:</span>
                    <span>{{ forgeOptions.sublimation.moneyNeed || 0 }}</span>
                  </div>
                  <div class="info-row">
                    <span>一键补满洗练石:</span>
                    <span>{{ forgeOptions.sublimation.washStoneNeed || 0 }}</span>
                  </div>
                  <div class="info-row">
                    <span>自定义升华%:</span>
                    <InputNumber
                      v-model="sublimationPercent"
                      :min="forgeOptions.sublimation.customMinPercent || 1"
                      :max="forgeOptions.sublimation.customMaxPercent || 100"
                      :step="1"
                      size="small"
                    />
                  </div>
                </div>
                <div class="action-buttons">
                  <Button
                    type="primary"
                    size="large"
                    :disabled="!(forgeOptions.sublimation && forgeOptions.sublimation.canDo)"
                    :loading="forgeSubmitting"
                    @click="submitSublimation(false)"
                  >
                    自定义升华
                  </Button>
                  <Button
                    type="success"
                    size="large"
                    :disabled="!(forgeOptions.sublimation && forgeOptions.sublimation.canDo)"
                    :loading="forgeSubmitting"
                    @click="submitSublimation(true)"
                  >
                    一键补满
                  </Button>
                </div>
              </div>
            </template>

            <div class="no-selection" v-else>
              <Icon type="ios-cube-outline" size="64" />
              <h3>选择法宝开始操作</h3>
              <p>从左侧选择一件法宝</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 委托锻造 -->
      <div v-if="activeMainTab === 'delegating'" class="tab-content">
        <div class="list-container">
          <div class="list-header">
            <h4>委托锻造中</h4>
            <Button type="text" size="small" @click="loadDelegatingList">
              <Icon type="ios-refresh" /> 刷新
            </Button>
          </div>

          <div class="delegating-list">
            <div
              v-for="(item, index) in delegatingList"
              :key="item.id || index"
              :class="['delegating-item', { 'can-receive': item.receive === 0 }]"
            >
              <div class="delegating-header">
                <div class="delegating-name">{{ item.name }}</div>
                <span v-if="item.receive === 0" class="status-tag waiting">待领取</span>
                <span v-else class="status-tag processing">处理中</span>
              </div>
              <div class="delegating-info">
                <div class="info-row">
                  <span class="label">类型:</span>
                  <span>{{ item.businessName || getTaskTypeName(item.type) }}</span>
                </div>
                <div class="info-row">
                  <span class="label">结果:</span>
                  <span>{{ item.result || '锻造中...' }}</span>
                </div>
                <div class="info-row">
                  <span class="label">完成时间:</span>
                  <span>{{ item.linTime || item.finishTimeText || '--' }}</span>
                </div>
              </div>
              <div class="delegating-actions" v-if="item.receive === 0">
                <Button
                  type="success"
                  size="small"
                  :loading="receivingIndex === index"
                  @click="receiveItem(item, index)"
                >
                  领取成品
                </Button>
              </div>
            </div>

            <div class="empty-state" v-if="delegatingList.length === 0">
              <Icon type="ios-hammer-outline" size="32" />
              <p>暂无委托锻造任务</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 历史记录 -->
      <div v-if="activeMainTab === 'history'" class="tab-content">
        <div class="list-container">
          <div class="list-header">
            <h4>锻造历史</h4>
            <Select v-model="historyFilter" size="small" style="width: 120px">
              <Option value="all">全部记录</Option>
              <Option value="success">成功记录</Option>
              <Option value="failed">失败记录</Option>
            </Select>
          </div>

          <div class="history-list">
            <div
              v-for="(item, index) in filteredHistory"
              :key="item.id || index"
              :class="['history-item', { received: item.receive === 1 }]"
            >
              <div class="history-row">
                <div class="history-name">{{ item.name }}</div>
                <div class="history-type">{{ item.businessName || getTaskTypeName(item.type) }}</div>
                <span :class="['status-tag', isForgeSuccess(item) ? 'success' : 'failed']">
                  {{ isForgeSuccess(item) ? '成功' : '失败' }}
                </span>
                <div class="history-time">{{ formatHistoryTime(item.linTime || item.finishTimeText) }}</div>
                <Button v-if="item.receive === 0" type="success" size="small" @click="receiveItem(item, index)">
                  领取
                </Button>
              </div>
            </div>

            <div class="empty-state" v-if="filteredHistory.length === 0">
              <Icon type="ios-list-outline" size="32" />
              <p>暂无锻造历史</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ForgingRoom',
  props: {
    level: { type: Number, default: 1 },
    points: { type: Number, default: 0 },
    successBonus: { type: Number, default: 5 }
  },
  data() {
    return {
      activeMainTab: 'blueprints',
      activeSubTab: 'smelt',
      mainTabs: [
        { key: 'blueprints', label: '图纸锻造', icon: 'ios-hammer', count: 0 },
        { key: 'equipment', label: '法宝重铸', icon: 'ios-color-wand', count: 0 },
        { key: 'delegating', label: '委托锻造', icon: 'ios-time', count: 0 },
        { key: 'history', label: '历史记录', icon: 'ios-list', count: 0 }
      ],
      equipmentSubTabs: [
        { key: 'smelt', label: '法宝熔炼' },
        { key: 'treasure', label: '秘宝打造' },
        { key: 'element', label: '五行轮转' },
        { key: 'sublimation', label: '淬炼升华' }
      ],

      // 图纸相关
      blueprintList: [],
      blueprintSearch: '',
      blueprintFilter: 'all',
      blueprintFilters: [
        { value: 'all', label: '全部' },
        { value: 'canForge', label: '可锻造' }
      ],
      selectedBlueprint: null,
      selectedBlueprintIndex: -1,
      itemFucaiNum: [],
      formulaMatZiList: [],
      forging: false,

      // 装备相关
      equipmentList: [],
      equipmentSearch: '',
      equipmentLoading: false,
      selectedEquip: null,
      forgeOptions: {},
      forgeSubmitting: false,

      // 熔炼相关
      smeltMaterials: [],
      materialSearch: '',
      materialLoading: false,
      selectedSmeltMaterialId: '',

      // 秘宝相关
      treasureBattleType: 'PVE',

      // 升华相关
      sublimationPercent: 1,

      // 委托相关
      delegatingList: [],
      receivingIndex: null,

      // 历史相关
      historyList: [],
      historyFilter: 'all'
    }
  },
  computed: {
    filteredBlueprints() {
      let list = this.blueprintList
      if (this.blueprintSearch) {
        const search = this.blueprintSearch.toLowerCase()
        list = list.filter(item =>
          (item.itemName || '').toLowerCase().includes(search)
        )
      }
      if (this.blueprintFilter === 'canForge') {
        list = list.filter(item => item.isMeet)
      }
      return list
    },
    filteredEquipments() {
      let list = this.equipmentList
      // 根据当前标签页筛选装备
      if (this.activeSubTab === 'smelt') {
        list = list.filter(item => this.canSmeltEquipment(item))
      } else if (this.activeSubTab === 'treasure') {
        list = list.filter(item => this.canTreasureForgeEquipment(item))
      } else if (this.activeSubTab === 'element') {
        list = list.filter(item => this.canElementRotateEquipment(item))
      } else if (this.activeSubTab === 'sublimation') {
        list = list.filter(item => this.canSublimationEquipment(item))
      }
      const search = this.equipmentSearch.trim().toLowerCase()
      if (search) {
        list = list.filter(item =>
          (item.itemName || '').toLowerCase().includes(search)
        )
      }
      return list
    },
    filteredSmeltMaterials() {
      let list = this.smeltMaterials
      const search = this.materialSearch.trim().toLowerCase()
      if (search) {
        list = list.filter(item =>
          (item.itemName || '').toLowerCase().includes(search)
        )
      }
      return [...list].sort((a, b) => Number(b.itemNum || 0) - Number(a.itemNum || 0))
    },
    filteredHistory() {
      let list = this.historyList
      if (this.historyFilter === 'success') {
        list = list.filter(item => this.isForgeSuccess(item))
      } else if (this.historyFilter === 'failed') {
        list = list.filter(item => !this.isForgeSuccess(item))
      }
      return list
    }
  },
  mounted() {
    this.loadBlueprintList()
    this.loadDelegatingList()
  },
  methods: {
    switchMainTab(tab) {
      this.activeMainTab = tab
      if (tab === 'equipment' && this.equipmentList.length === 0) {
        this.loadEquipmentList()
      }
      if (tab === 'delegating') {
        this.loadDelegatingList()
      }
    },
    switchSubTab(key) {
      this.activeSubTab = key
      this.selectedEquip = null
    },

    // 图纸相关
    async loadBlueprintList() {
      try {
        const res = await this.$http.post(
          `/gameCharaEquip/queryLearnedFormulaList?charaId=${this.getCookie('charaId')}&formulaType=1`
        )
        if (res.data.status === 200) {
          this.blueprintList = (res.data.data || []).map(this.normalizeBlueprint)
          this.updateTabCounts()
        }
      } catch (err) {
        this.$Message.warning('获取图纸列表失败')
      }
    },
    normalizeBlueprint(item) {
      const bp = { ...(item || {}) }
      bp.formulaId = bp.formulaId || bp.id || ''
      bp.itemName = bp.itemName || bp.name || ''
      return bp
    },
    async selectBlueprint(item, index) {
      this.selectedBlueprintIndex = index
      try {
        const res = await this.$http.post(
          `/gameCharaEquip/queryForgingOrPills?charaId=${this.getCookie('charaId')}&packItemIds=${item.formulaId}&formulaType=1`
        )
        if (res.data.status === 200) {
          const data = res.data.data
          this.selectedBlueprint = this.normalizeBlueprint({
            ...item,
            ...data,
            formulaId: item.formulaId
          })
          this.initBlueprintMaterials()
        }
      } catch (err) {
        this.$Message.warning('获取图纸详情失败')
      }
    },
    initBlueprintMaterials() {
      this.itemFucaiNum = []
      this.formulaMatZiList = []
      if (!this.selectedBlueprint || !this.selectedBlueprint.matList) return

      this.selectedBlueprint.matList.forEach((mat, idx) => {
        if (this.isOptionalMaterial(mat)) {
          this.itemFucaiNum[idx] = Math.min(mat.haveNum, mat.num)
          this.formulaMatZiList.push({
            itemId: mat.itemId,
            haveNum: this.itemFucaiNum[idx]
          })
        }
      })
    },
    getMaterialType(material) {
      if (!material) return 1
      const rawType = material.type != null
        ? material.type
        : (material.materialType != null ? material.materialType : material.matType)
      const type = Number(String(rawType == null ? '' : rawType).trim())
      return Number.isFinite(type) ? type : 1
    },
    isOptionalMaterial(material) {
      return this.getMaterialType(material) === 2
    },
    isMaterialFullyProvided(material) {
      return Number(material && material.haveNum || 0) >= Number(material && material.num || 0)
    },
    getMaterialStatusIcon(material) {
      if (this.isMaterialFullyProvided(material)) return 'ios-checkmark-circle'
      return this.isOptionalMaterial(material) ? 'ios-add-circle-outline' : 'ios-close-circle'
    },
    getMaterialStatusColor(material) {
      if (this.isMaterialFullyProvided(material)) return '#19be6b'
      return this.isOptionalMaterial(material) ? '#ff9900' : '#ed4014'
    },
    handleMaterialChange(itemId, index, val) {
      const found = this.formulaMatZiList.find(f => f.itemId === itemId)
      if (found) {
        found.haveNum = val || 0
      } else {
        this.formulaMatZiList.push({
          itemId,
          haveNum: val || 0
        })
      }
    },
    async startForging() {
      if (!this.selectedBlueprint || !this.selectedBlueprint.isMeet) return
      this.forging = true
      try {
        const res = await this.$http.post('/gameCharaEquip/forgingEquipmentOrPills', {
          charaId: this.getCookie('charaId'),
          packItemIds: this.selectedBlueprint.formulaId,
          matList: this.formulaMatZiList,
          type: 1
        })
        if (res.data.status === 200) {
          this.$Message.success(res.data.data || res.data.msg || '锻造成功')
          this.selectedBlueprint = null
          this.loadDelegatingList()
          this.activeMainTab = 'delegating'
          this.$emit('forged')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '锻造失败')
      } finally {
        this.forging = false
      }
    },

    // 装备相关
    async loadEquipmentList() {
      this.equipmentLoading = true
      try {
        const res = await this.$http.post(
          `/gameChara/getCharaPackage?charaId=${this.getCookie('charaId')}`
        )
        if (res.data.status === 200) {
          this.equipmentList = (res.data.data || []).filter(item => this.isForgeEquipment(item))
          this.updateTabCounts()
        }
      } catch (err) {
        this.$Message.warning('获取装备列表失败')
      } finally {
        this.equipmentLoading = false
      }
    },
    isForgeEquipment(item) {
      const kind = Number(item && (item.kind != null ? item.kind : item.geKind))
      const typeDec = item && item.typeDec
      const equipKinds = [1, 2, 3, 4, 7, 8, 9, 10, 11]
      const equipTypes = ['武器', '玄端', '头冠', '项链', '戒指', '绶佩', '绶带', '鞜履', '法宝']
      return equipKinds.includes(kind) ||
        equipTypes.includes(typeDec) ||
        (item && Number(item.itemType) === 1) ||
        (item && Number(item.equitId) > 0)
    },
    canSmeltEquipment(item) {
      const kind = Number(item && (item.kind != null ? item.kind : item.geKind))
      return kind === 11 || (item && item.typeDec === '法宝')
    },
    canTreasureForgeEquipment(item) {
      const phase = Number(item && item.treasurePhase || 0)
      return (String(item.color) === '6' || String(item.color) === '7') && phase === 0
    },
    canElementRotateEquipment(item) {
      return !this.canSmeltEquipment(item)
    },
    canSublimationEquipment(item) {
      const currentRange = Number(item.enhanRange || 0)
      const enhanLevel = Number(item.enhanLevel || 0)
      const maxStrengthen = Number(item.maxStrengthen || 0)
      const maxRange = enhanLevel / 10
      return maxStrengthen > 0 && enhanLevel >= maxStrengthen && currentRange < maxRange
    },
    async selectEquipment(item, index) {
      this.selectedEquip = item
      this.forgeOptions = {}
      if (this.activeSubTab !== 'smelt') {
        await this.loadForgeOptions(item.packItemId)
      }
      if (this.activeSubTab === 'smelt') {
        await this.loadSmeltMaterials()
      }
    },
    async loadForgeOptions(packItemId) {
      try {
        const res = await this.$http.post(
          `/gameCharaEquip/queryForgeEquipOptions?charaId=${this.getCookie('charaId')}&packItemId=${packItemId}`
        )
        if (res.data.status === 200) {
          this.forgeOptions = res.data.data || {}
          const treasure = this.forgeOptions.treasureForge || {}
          this.treasureBattleType = treasure.defaultBattleType || 'PVE'
          const sub = this.forgeOptions.sublimation || {}
          this.sublimationPercent = sub.customMinPercent || 1
        }
      } catch (err) {
        this.$Message.warning('获取锻造选项失败')
      }
    },
    async loadSmeltMaterials() {
      this.materialLoading = true
      try {
        const res = await this.$http.post(
          `/gameChara/getCharaMaterial/?charaId=${this.getCookie('charaId')}`
        )
        if (res.data.status === 200) {
          this.smeltMaterials = (res.data.data || []).filter(item => Number(item.itemType) === 10)
          if (!this.selectedSmeltMaterialId && this.smeltMaterials.length > 0) {
            this.selectedSmeltMaterialId = this.smeltMaterials[0].packItemId
          }
        }
      } catch (err) {
        this.$Message.warning('获取熔炼材料失败')
      } finally {
        this.materialLoading = false
      }
    },
    async submitSmelt() {
      if (!this.selectedEquip || !this.selectedSmeltMaterialId) return
      this.forgeSubmitting = true
      try {
        const res = await this.$http.post(
          `/gameCharaEquip/smeltEquitBypackage?charaId=${this.getCookie('charaId')}&packItemId=${this.selectedEquip.packItemId}&packItemId2=${this.selectedSmeltMaterialId}`
        )
        if (res.data.status === 200) {
          this.$Message.success(res.data.data || res.data.msg || '熔炼成功')
          this.loadEquipmentList()
          this.$emit('smelted')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '熔炼失败')
      } finally {
        this.forgeSubmitting = false
      }
    },
    async submitTreasureForge() {
      if (!this.selectedEquip) return
      if (this.needsTreasureForgeLimitWarning()) {
        const warning = this.getTreasureForgeLimitWarning()
        const run = () => this.doSubmitTreasureForge()
        if (this.$Modal && this.$Modal.confirm) {
          this.$Modal.confirm({
            title: '确认秘宝打造',
            content: '<p>' + warning + '</p>',
            okText: '确认打造',
            cancelText: '再考虑下',
            onOk: run
          })
        } else if (window.confirm(warning)) {
          run()
        }
        return
      }
      this.doSubmitTreasureForge()
    },
    async doSubmitTreasureForge() {
      if (!this.selectedEquip || this.forgeSubmitting) return
      this.forgeSubmitting = true
      try {
        const res = await this.$http.post(
          `/gameCharaEquip/startTreasureForge?charaId=${this.getCookie('charaId')}&packItemId=${this.selectedEquip.packItemId}&battleType=${encodeURIComponent(this.treasureBattleType)}`
        )
        if (res.data.status === 200) {
          this.$Message.success('秘宝打造已开始')
          this.loadEquipmentList()
          this.loadDelegatingList()
          this.activeMainTab = 'delegating'
          this.$emit('treasureForged')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '秘宝打造失败')
      } finally {
        this.forgeSubmitting = false
      }
    },
    async submitElementRotate() {
      if (!this.selectedEquip) return
      this.forgeSubmitting = true
      try {
        const res = await this.$http.post(
          `/gameCharaEquip/startElementRotate?charaId=${this.getCookie('charaId')}&packItemId=${this.selectedEquip.packItemId}`
        )
        if (res.data.status === 200) {
          this.$Message.success('五行轮转已开始')
          this.loadEquipmentList()
          this.loadDelegatingList()
          this.activeMainTab = 'delegating'
          this.$emit('elementRotated')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '五行轮转失败')
      } finally {
        this.forgeSubmitting = false
      }
    },
    async submitSublimation(fillToFull) {
      if (!this.selectedEquip) return
      this.forgeSubmitting = true
      const url = fillToFull
        ? `/gameCharaEquip/doEquipSublimation?charaId=${this.getCookie('charaId')}&packItemId=${this.selectedEquip.packItemId}&fillToFull=1`
        : `/gameCharaEquip/doEquipSublimation?charaId=${this.getCookie('charaId')}&packItemId=${this.selectedEquip.packItemId}&targetPercent=${this.sublimationPercent}&fillToFull=0`
      try {
        const res = await this.$http.post(url)
        if (res.data.status === 200) {
          this.$Message.success('升华成功')
          await this.loadEquipmentList()
          const updated = this.equipmentList.find(e => e.packItemId === this.selectedEquip.packItemId)
          if (updated) this.selectedEquip = updated
          await this.loadForgeOptions(this.selectedEquip.packItemId)
          this.$emit('sublimated')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '升华失败')
      } finally {
        this.forgeSubmitting = false
      }
    },

    // 委托相关
    async loadDelegatingList() {
      try {
        const [oldRes, newRes] = await Promise.all([
          this.$http.post(`/gameCharaEquip/queryForgingPlayer?charaId=${this.getCookie('charaId')}&formulaType=1`),
          this.$http.post(`/gameCharaEquip/queryForgeTaskList?charaId=${this.getCookie('charaId')}`)
        ])
        const oldList = (oldRes.data && oldRes.data.data) || []
        const newList = ((newRes.data && newRes.data.data) || []).map(item => ({
          ...item,
          finishTimeText: this.formatDateTime(item.finishTime),
          remainText: this.formatDuration(item.remainSeconds || 0)
        }))
        const merged = [...oldList, ...newList]
        this.delegatingList = merged.filter(item => item.receive === 0)
        this.historyList = merged.filter(item => item.receive !== 0)
        this.updateTabCounts()
      } catch (err) {
        console.error('获取委托列表失败', err)
      }
    },
    async receiveItem(item, index) {
      this.receivingIndex = index
      try {
        const url = item.type === 3 || item.type === 4
          ? `/gameCharaEquip/receiveForgeTask?charaId=${this.getCookie('charaId')}&taskId=${item.id}`
          : `/gameCharaEquip/receiveForgingPlayer?charaId=${this.getCookie('charaId')}&forPlayerId=${item.id}`
        const res = await this.$http.post(url)
        if (res.data.status === 200) {
          this.$Message.success('领取成功')
          this.loadDelegatingList()
          this.$emit('received')
        } else {
          this.$Message.warning(res.data.msg)
        }
      } catch (err) {
        this.$Message.warning((err.data && err.data.msg) || '领取失败')
      } finally {
        this.receivingIndex = null
      }
    },

    // 工具方法
    updateTabCounts() {
      this.mainTabs[0].count = this.blueprintList.length
      this.mainTabs[1].count = this.equipmentList.length
      this.mainTabs[2].count = this.delegatingList.length
      this.mainTabs[3].count = this.historyList.length
    },
    getTaskTypeName(type) {
      if (type === 3) return '秘宝打造'
      if (type === 4) return '五行轮转'
      return '法宝打造'
    },
    isForgeSuccess(item) {
      if (!item) return false
      const result = String(item.result || '')
      if (/(失败|fail|error)/i.test(result)) return false
      if (/(成功|完成|已领取)/i.test(result)) return true
      return item.receive === 1
    },
    getElementName(el) {
      const map = { 1: '金', 2: '木', 3: '水', 4: '火', 5: '土', 6: '风', 7: '雷', 8: '冰' }
      return map[el] || '未知'
    },
    getTreasureForgeRuleText() {
      const treasureForge = (this.forgeOptions && this.forgeOptions.treasureForge) || {}
      if (Number(treasureForge.targetTreasurePhase || 0) === 1) {
        return '灵宝 + 天外陨铁 = 后天秘宝，基础属性提升 20%'
      }
      if (Number(treasureForge.targetTreasurePhase || 0) === 2) {
        return '已鉴定灵宝 + 天外陨铁 + 额外材料 = 先天秘宝，装备全属性随机提升 10-30%'
      }
      return '秘宝打造将直接改写原装备属性'
    },
    needsTreasureForgeLimitWarning() {
      const treasureForge = (this.forgeOptions && this.forgeOptions.treasureForge) || {}
      return Number(treasureForge.targetTreasurePhase || 0) === 2
    },
    getTreasureForgeLimitWarning() {
      const cultivated = this.isCultivatedTreasureBase(this.selectedEquip)
      const prefix = cultivated ? '当前灵宝已有强化或洗练培养，' : ''
      return prefix + '打造为先天秘宝后，装备全属性会随机提升 10-30%，但不能继续强化和洗练，请确认后再发起打造。'
    },
    isCultivatedTreasureBase(item) {
      if (!item) return false
      return Number(item.enhanLevel || 0) > 0 ||
        Number(item.enhanRange || 0) > 0 ||
        Number(item.appraisaScore || item.appraisalScore || 0) > 0
    },
    formatDuration(seconds) {
      const total = Number(seconds) || 0
      if (total <= 0) return '即时'
      const hour = Math.floor(total / 3600)
      const minute = Math.floor((total % 3600) / 60)
      const second = total % 60
      if (hour > 0) return hour + '时' + minute + '分'
      if (minute > 0) return minute + '分' + second + '秒'
      return second + '秒'
    },
    formatDateTime(value) {
      if (!value) return '--'
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      const pad = function(num) { return String(num).padStart(2, '0') }
      return pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes())
    },
    formatHistoryTime(value) {
      if (!value) return '--'
      // 已经是 MM-DD HH:mm 格式的直接返回
      if (/^\d{2}-\d{2}\s\d{2}:\d{2}$/.test(value)) return value
      // 完整日期格式截取 MM-DD HH:mm
      if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        return value.substring(5, 16)
      }
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      const pad = function(num) { return String(num).padStart(2, '0') }
      return pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes())
    },
    getCookie(name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) return parts.pop().split(';').shift()
      return ''
    },
    hasBaseStats(item) {
      return item.life || item.mana || item.attack || item.magAttack || item.defense || item.magicDefense
    }
  }
}
</script>

<style scoped>
.forging-room {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.room-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #2db7f5 0%, #667eea 100%);
  border-radius: 12px 12px 0 0;
  color: #fff;
  flex-shrink: 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.room-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.room-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.room-desc {
  margin: 4px 0 0;
  font-size: 13px;
  opacity: 0.9;
}

.header-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-label {
  font-size: 11px;
  opacity: 0.8;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
}

.room-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.main-tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  overflow-x: auto;
  flex-shrink: 0;
}

.main-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #808695;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  transition: all 0.2s;
}

.main-tab:hover {
  color: #2d8cf0;
  background: #f0f7ff;
}

.main-tab.active {
  color: #2d8cf0;
  border-bottom-color: #2d8cf0;
}

.tab-count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #edf4ff;
  color: #2d8cf0;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tab-content {
  flex: 1;
  overflow: hidden;
}

.forge-layout,
.equipment-layout {
  display: flex;
  height: 100%;
}

.blueprint-list,
.equipment-list {
  width: 280px;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.list-header {
  padding: 12px;
  border-bottom: 1px solid #e8e8e8;
  background: #f8f9fa;
}

.filter-tags {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.filter-tag {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  cursor: pointer;
  background: #f5f5f5;
  color: #808695;
  transition: all 0.2s;
}

.filter-tag:hover {
  color: #2d8cf0;
  background: #edf4ff;
}

.filter-tag.active {
  background: #2d8cf0;
  color: #fff;
}

.blueprint-items,
.equipment-items {
  flex: 1;
  overflow-y: auto;
  padding: 10px 10px 40px;
}

.blueprint-item,
.equipment-item {
  padding: 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.blueprint-item:hover,
.equipment-item:hover {
  border-color: #2d8cf0;
  background: #f0f7ff;
}

.blueprint-item.active,
.equipment-item.active {
  border-color: #2d8cf0;
  background: #e6f7ff;
}

.blueprint-item.can-forge {
  border-left: 3px solid #19be6b;
}

.blueprint-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.blueprint-name,
.equip-name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.equip-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
}

.equip-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.status-tag {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
}

.status-tag.success {
  background: #f6ffed;
  color: #19be6b;
}

.status-tag.failed {
  background: #fff1f0;
  color: #ed4014;
}

.status-tag.waiting {
  background: #f6ffed;
  color: #19be6b;
}

.status-tag.processing {
  background: #fff7e6;
  color: #fa8c16;
}

.blueprint-info,
.equip-info {
  font-size: 12px;
  color: #808695;
}

.equip-enhance {
  font-size: 13px;
  font-weight: 600;
  color: #ff9900;
  margin-left: 4px;
}

.equip-range {
  font-size: 11px;
  color: #19be6b;
  margin-left: 4px;
  padding: 1px 4px;
  background: #f0faf4;
  border-radius: 3px;
}

.equip-base-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.stat-tag {
  font-size: 11px;
  color: #515a6e;
  padding: 1px 5px;
  background: #f8f8f9;
  border-radius: 3px;
  white-space: nowrap;
}

.equip-shen-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.shen-tag {
  font-size: 11px;
  color: #722ed1;
  padding: 1px 5px;
  background: #f9f0ff;
  border-radius: 3px;
  white-space: nowrap;
}

.blueprint-detail,
.equipment-detail {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
}

.detail-header {
  margin-bottom: 20px;
}

.detail-header h3 {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 700;
  color: #1f2d3d;
}

.detail-desc {
  font-size: 14px;
  color: #808695;
  line-height: 1.6;
}

.materials-card,
.action-panel {
  background: #fff;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #e8e8e8;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2d3d;
  margin-bottom: 16px;
}

.material-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid #e8e8e8;
}

.material-item.sufficient {
  border-color: #b7eb8f;
  background: #f6ffed;
}

.material-item.insufficient {
  border-color: #ffa39e;
  background: #fff1f0;
}

.material-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.material-type {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #e6f7ff;
  color: #2d8cf0;
}

.material-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2d3d;
}

.material-quantity {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.material-input {
  width: 100px;
}

.forge-action {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #e8e8e8;
}

.action-info {
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #808695;
}

.action-hint {
  margin-top: 10px;
  font-size: 13px;
  text-align: center;
}

.action-hint.warning {
  color: #ed4014;
}

.sub-tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  padding: 0 16px;
}

.sub-tab {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #808695;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.sub-tab:hover {
  color: #2d8cf0;
}

.sub-tab.active {
  color: #2d8cf0;
  border-bottom-color: #2d8cf0;
}

.smelt-materials {
  margin-bottom: 16px;
}

.material-search {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.material-list {
  max-height: 300px;
  overflow-y: auto;
}

.smelt-material {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid #e8e8e8;
  cursor: pointer;
  transition: all 0.2s;
}

.smelt-material:hover {
  border-color: #2d8cf0;
}

.smelt-material.active {
  border-color: #ff8f1f;
  background: #fff7e6;
}

.mat-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2d3d;
}

.mat-count {
  font-size: 13px;
  font-weight: 600;
  color: #2d8cf0;
}

.treasure-info,
.element-info,
.sublimation-info {
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.info-row span:first-child {
  color: #808695;
}

.info-row span:last-child {
  font-weight: 500;
  color: #1f2d3d;
}

.info-row .insufficient {
  color: #ed4014;
}

.treasure-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 10px 0;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid rgba(237, 64, 20, 0.2);
  background: rgba(237, 64, 20, 0.08);
  color: #c43f2f;
  font-size: 13px;
  line-height: 1.5;
}

.treasure-warning .ivu-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.action-buttons .ivu-btn {
  flex: 1;
}

.list-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.delegating-list,
.history-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.delegating-item,
.history-item {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 6px;
}

.history-item.received {
  border-left: 4px solid #19be6b;
}

.history-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.history-row .history-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-row .history-type {
  font-size: 12px;
  color: #808695;
  white-space: nowrap;
  flex-shrink: 0;
}

.history-row .status-tag {
  flex-shrink: 0;
}

.history-row .history-time {
  font-size: 12px;
  color: #808695;
  white-space: nowrap;
  flex-shrink: 0;
}

.no-selection {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #808695;
}

.no-selection h3 {
  margin: 20px 0 10px;
  color: #1f2d3d;
  font-size: 20px;
}

.no-selection p {
  font-size: 14px;
}

.empty-state {
  text-align: center;
  padding: 30px;
  color: #808695;
}

@media (max-width: 768px) {
  .room-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-stats {
    width: 100%;
    justify-content: space-around;
  }

  .forge-layout,
  .equipment-layout {
    flex-direction: column;
  }

  .blueprint-list,
  .equipment-list {
    width: 100%;
    max-height: 300px;
    border-right: none;
    border-bottom: 1px solid #e8e8e8;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/cave/ForgingRoom.vue